/**
 * The agreement box (legal-pages tickets 03 and 07): an unticked checkbox, a
 * sentence naming the documents as links, and the prompt shown when its button
 * is pressed without it. Sign-in, the in-app agreement step and the checkout
 * confirmation all draw this one, so the three cannot drift.
 *
 * Never pre-ticked and never remembered: the caller holds `checked` in plain
 * `useState`, starting false.
 *
 * The button beside it is never greyed out. Pressed with the box unticked, it
 * calls `requireAgreement`, which focuses the box and has the caller show the
 * prompt (`role="alert"`, in a line the row always keeps, so nothing moves).
 * Ticking clears it. A dialog's own error (a save that failed) is said in that
 * same line (`note`), so it needs no second reserved line.
 *
 * Every link opens in a new tab: the screen behind holds a typed email, a
 * chosen file or a plan choice that a navigation would lose.
 */
import React from 'react';
import { legal } from '../lib/legal';
import { AGREEMENT_DOCUMENTS, TICK_TO_CONTINUE } from '../lib/notices';
import './LegalNotices.css';

/** A document's name as a link to its page, in a new tab. */
export function LegalDocLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a className="cj-legal-inline-link" href={href} target="_blank" rel="noopener">
      {children}
      <span className="cj-sr-only"> (opens in a new tab)</span>
    </a>
  );
}

/** The documents an agreement sentence can name that have a page today, in the sentence's order. */
export function agreementDocuments(): { slug: string; short: string; full: string; path: string }[] {
  return AGREEMENT_DOCUMENTS.flatMap((d) => {
    const page = legal.documents.find((doc) => doc.slug === d.slug);
    return page ? [{ ...d, path: page.path }] : [];
  });
}

/** "Terms of Service", "Terms of Service and Privacy Policy", "A, B and C": each a link. */
export function AgreementDocLinks({ names }: { names: 'short' | 'full' }) {
  const docs = agreementDocuments();
  return (
    <>
      {docs.map((d, i) => (
        <React.Fragment key={d.slug}>
          {i === 0 ? '' : i === docs.length - 1 ? ' and ' : ', '}
          <LegalDocLink href={d.path}>{d[names]}</LegalDocLink>
        </React.Fragment>
      ))}
    </>
  );
}

export interface AgreementBoxProps {
  /** The checkbox's id; the prompt's is `<id>-prompt`. Also the driver hook, `data-agree`. */
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Whether "Tick the box to continue" shows. */
  prompt: boolean;
  /** The caller's own error (a failed save), said in the same line while the prompt is not showing. A ticked box and a failed save never coincide with the prompt. */
  note?: string;
  /** While the caller's write is in flight. */
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  /** In a dialog: the box takes first focus (`LegalDialog` focuses `[data-first-focus]`), so Tab runs box, link, buttons. Enter on a checkbox does nothing, so a held Enter cannot carry through. */
  firstFocus?: boolean;
  /** The sentence, links included. */
  children: React.ReactNode;
}

export function AgreementBox(p: AgreementBoxProps) {
  const promptId = p.id + '-prompt';
  return (
    <div className={'cj-agree cj-legal-inline' + (p.prompt ? ' cj-agree--error' : '')} data-agree={p.id}>
      <label className="cj-agree-row" htmlFor={p.id}>
        <input
          ref={p.inputRef} id={p.id} type="checkbox" checked={p.checked} disabled={p.disabled}
          aria-invalid={p.prompt || undefined} aria-describedby={promptId} data-first-focus={p.firstFocus ? '' : undefined}
          onChange={(e) => p.onChange(e.target.checked)}
        />
        <span>{p.children}</span>
      </label>
      {/* Outside the label, so it is not part of the box's name; always mounted, so the text arriving is announced. */}
      <span id={promptId} role="alert" className="cj-agree-prompt" data-agree-prompt="">{p.prompt ? TICK_TO_CONTINUE : p.note ?? ''}</span>
    </div>
  );
}

/**
 * The state every agreement box needs, in one place: `agreed`, whether the
 * prompt shows, the box's ref, and `requireAgreement()` for the button — true
 * when ticked; otherwise it shows the prompt, focuses the box and answers false.
 * `reset()` unticks it.
 */
export function useAgreement() {
  const [agreed, setAgreedState] = React.useState(false);
  const [prompt, setPrompt] = React.useState(false);
  const ref = React.useRef<HTMLInputElement>(null);
  const setAgreed = React.useCallback((v: boolean) => { setAgreedState(v); if (v) setPrompt(false); }, []);
  const requireAgreement = React.useCallback(() => {
    if (ref.current?.checked) return true;
    setPrompt(true);
    ref.current?.focus();
    return false;
  }, []);
  /** Back to unticked with no prompt, for a screen that stays mounted across a step (sign-in to check-inbox and back). */
  const reset = React.useCallback(() => { setAgreedState(false); setPrompt(false); }, []);
  return { agreed, setAgreed, prompt, ref, requireAgreement, reset };
}
